import yaml from "js-yaml";
import { marked } from "marked";

const testimonialModules = import.meta.glob("../../_posts/testimonials/*.markdown", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

export type Testimonial = {
  id: string;
  name: string;
  role: string;
  quote: string;
  image?: string;
  company?: string;
  featured: boolean;
  date: Date;
};

function slugFromPath(path: string): string {
  const filename = path.split("/").pop() ?? "";
  const match = filename.match(/^\d{4}-\d{2}-\d{2}-(.+)\.markdown$/);
  return match?.[1] ?? filename.replace(/\.markdown$/, "");
}

function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  if (!raw.startsWith("---")) {
    return { data: {}, content: raw };
  }

  const end = raw.indexOf("\n---", 3);
  if (end === -1) {
    return { data: {}, content: raw };
  }

  const frontmatter = raw.slice(3, end);
  const content = raw.slice(end + 4).replace(/^\r?\n/, "");
  const data = yaml.load(frontmatter) as Record<string, unknown> | null;

  return { data: data ?? {}, content };
}

function parseString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function parseBoolean(value: unknown): boolean {
  return value === true || value === "true";
}

function quoteFromContent(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) return "";
  const html = marked.parse(trimmed, { async: false }) as string;
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

function parseTestimonial(path: string, raw: string): Testimonial | null {
  const { data, content } = parseFrontmatter(raw);
  const name = parseString(data.name);
  const role = parseString(data.role);
  const quoteFromFm = parseString(data.quote);
  const quote = quoteFromFm || quoteFromContent(content);

  if (!name || !quote) return null;

  const dateRaw = parseString(data.date);
  const date = dateRaw ? new Date(dateRaw) : new Date(0);

  return {
    id: slugFromPath(path),
    name,
    role,
    quote,
    image: parseString(data.image) || undefined,
    company: parseString(data.company) || undefined,
    featured: parseBoolean(data.featured),
    date,
  };
}

let cached: Testimonial[] | null = null;

export function getTestimonials(): Testimonial[] {
  if (cached) return cached;

  const items: Testimonial[] = [];

  for (const [path, raw] of Object.entries(testimonialModules)) {
    const testimonial = parseTestimonial(path, raw);
    if (testimonial) items.push(testimonial);
  }

  items.sort((a, b) => b.date.getTime() - a.date.getTime());
  cached = items;
  return items;
}

export function getFeaturedTestimonials(limit = 2): Testimonial[] {
  const featured = getTestimonials().filter((t) => t.featured);
  if (featured.length >= limit) return featured.slice(0, limit);
  return getTestimonials().slice(0, limit);
}

export function getTestimonialById(id: string): Testimonial | undefined {
  return getTestimonials().find((t) => t.id === id);
}
